/**
 * The Roll Log (CONTEXT.md, "Roll Log"): the ordered record of a Session's Play Actions, Rolls
 * included, kept on the Character beside the Session (not inside it, so a start-fresh's inverse,
 * which holds the whole previous Session, does not hold the log too). Each entry carries the
 * action, its human-readable summary, the events it produced and the inverse that reverses it.
 * Reversal is itself a Play Action: the inverse is applied and logged as a new entry linked to
 * the original, which stays visible as reversed. Reversals are final (see ./log.ts
 * `isLive` and ADR-0012): with every inverse an absolute primitive, a redo cannot be exact once
 * anything earlier has been reversed, so an undone action is done again by acting again.
 * Everything here is pure; `src/character` applies the actions and saves.
 */
import { summarizeRoll } from "../rolls/model";
import type { PlayAction, SessionEvent } from "./actions";
import { isPrimitive } from "./actions";
import type { Session } from "./model";

/** The last entries kept per Session; the oldest go when the 501st arrives, counted in `dropped`. */
export const ROLL_LOG_CAP = 500;

/** What the filter sorts by: a Roll, a Session change, or a note the app added (a migration's removals, the cap). */
export type LogEntryType = "roll" | "session" | "note";

export interface LogEntry {
  /** "L<n>", unique within the log; n never repeats even after entries are dropped. */
  id: string;
  /** ISO timestamp. */
  at: string;
  /** The round the Session stood in once the action had applied; null outside an encounter. */
  round: number | null;
  /** The Play Action's kind, or "note". */
  kind: string;
  type: LogEntryType;
  summary: string;
  events: SessionEvent[];
  /** The action as applied (a Roll's full breakdown rides in it); null for a note. */
  action: PlayAction | null;
  /** Applying this reverses the entry; null for a note. */
  inverse: PlayAction | null;
  /** The id of the entry that reversed this one, once reversed. */
  reversedBy: string | null;
  /** For a reversal entry: the id of the entry it reversed. */
  reverses: string | null;
}

export interface RollLog {
  /** Oldest first. */
  entries: LogEntry[];
  /** How many older entries the cap dropped. */
  dropped: number;
  /** The next entry number. */
  counter: number;
}

export function emptyRollLog(): RollLog {
  return { entries: [], dropped: 0, counter: 1 };
}

export type NewLogEntry = Omit<LogEntry, "id" | "reversedBy"> & Partial<Pick<LogEntry, "reversedBy">>;

/** Append an entry, dropping the oldest beyond the cap. Never mutates the input. */
export function appendEntry(log: RollLog, entry: NewLogEntry, cap = ROLL_LOG_CAP): { log: RollLog; entry: LogEntry } {
  const full: LogEntry = { ...entry, id: `L${log.counter}`, reversedBy: entry.reversedBy ?? null };
  const entries = [...log.entries, full];
  const over = Math.max(0, entries.length - cap);
  return { log: { entries: entries.slice(over), dropped: log.dropped + over, counter: log.counter + 1 }, entry: full };
}

export function findEntry(log: RollLog, id: string): LogEntry | undefined {
  return log.entries.find((e) => e.id === id);
}

/** Mark `id` as reversed by `byId`. */
export function markReversed(log: RollLog, id: string, byId: string): RollLog {
  return { ...log, entries: log.entries.map((e) => (e.id === id ? { ...e, reversedBy: byId } : e)) };
}

/* ------------------------------------ Summaries ------------------------------------ */

/** One readable line for an entry: a Roll's summary, else the first event, else the action named. */
export function summarizeAction(action: PlayAction, events: readonly SessionEvent[]): string {
  if (action.kind === "roll") return summarizeRoll(action.roll);
  const event = events.find((e) => e.kind !== "blocked" && e.kind !== "roll-needed" && e.kind !== "iwr");
  return event?.text ?? describeAction(action);
}

/** The action in words when no event described it (primitives produce none). */
export function describeAction(action: PlayAction): string {
  switch (action.kind) {
    case "damage": return `${action.amount}${action.type && action.type !== "untyped" ? ` ${action.type}` : ""} damage${action.critical ? " (critical)" : ""}${action.source ? ` from ${action.source}` : ""}`;
    case "heal": return `${action.amount} healing${action.source ? ` from ${action.source}` : ""}`;
    case "set-temp-hp": return `Temporary HP ${action.value}`;
    case "apply-condition": return `${action.slug}${action.value !== undefined ? ` ${action.value}` : ""} applied`;
    case "reduce-condition": return "Condition reduced";
    case "clear-condition": return "Condition removed";
    case "dying-recovery": return `Recovery check: ${action.degree}`;
    case "apply-effect": return `${action.name} applied`;
    case "end-effect": return "Effect ended";
    case "set-badge": return `Effect counter ${action.value ?? "cleared"}`;
    case "advance": return `${action.seconds} seconds pass`;
    case "start-encounter": return "Encounter begins";
    case "end-encounter": return "Encounter ends";
    case "spend-slot": return action.rank === "0" ? "Cantrip cast from a staff" : `Rank ${action.rank} slot cast`;
    case "restore-slot": return `Rank ${action.rank} slot restored`;
    case "spend-innate": return "Innate spell cast";
    case "restore-innate": return "Innate spell use restored";
    case "spend-focus": return "Focus Point spent";
    case "refocus": return "Refocus";
    case "spend-hero-point": return "Hero Point spent";
    case "gain-hero-point": return "Hero Point gained";
    case "spend-resource": return `${action.key} spent`;
    case "restore-resource": return `${action.key} restored`;
    case "use-entry": return "Use spent";
    case "restore-entry": return "Use restored";
    case "use-consumable": return "Consumable used";
    case "fire-ammunition": return `Ammunition fired${action.count && action.count > 1 ? ` ×${action.count}` : ""}`;
    case "add-consumable-stack": return `${action.stack.name} ×${action.stack.quantity} added`;
    case "toggle": return `${action.key} ${action.state === null ? "reset" : action.state.checked === false ? "off" : "on"}`;
    case "daily-preparations": return "Daily preparations";
    case "start-fresh": return "Session started fresh";
    case "roll": return summarizeRoll(action.roll);
    case "set-hp": return `HP ${action.value}${action.temp > 0 ? ` (+${action.temp} temporary)` : ""}`;
    case "set-hero-points": return `Hero Points ${action.value}`;
    case "set-focus": return `Focus Points ${action.value}`;
    case "set-clock": return action.round === null ? "Clock set" : `Round ${action.round}`;
    case "replace": return "Session replaced";
    case "batch": return action.label;
    default: return action.kind.replace(/-/g, " ");
  }
}

/**
 * What changed between two Sessions, in words, for a reversal entry (its primitives produce no
 * events): HP, Hero Points, focus, the Conditions and Effects that came or went, the clock.
 */
export function describeSessionChange(before: Session, after: Session): SessionEvent[] {
  const out: SessionEvent[] = [];
  if (before.hp.value !== after.hp.value || before.hp.temp !== after.hp.temp) {
    out.push({ kind: "hp", text: `HP ${before.hp.value} → ${after.hp.value}${before.hp.temp !== after.hp.temp ? `, temporary HP ${before.hp.temp} → ${after.hp.temp}` : ""}` });
  }
  for (const c of before.conditions) {
    const still = after.conditions.find((x) => x.id === c.id);
    if (!still) out.push({ kind: "condition", text: `${c.name}${c.value !== null ? ` ${c.value}` : ""} removed` });
    else if (still.value !== c.value) out.push({ kind: "condition", text: `${c.name} ${c.value ?? ""} → ${still.value ?? ""}`.replace(/\s+/g, " ") });
  }
  for (const c of after.conditions) if (!before.conditions.some((x) => x.id === c.id)) out.push({ kind: "condition", text: `${c.name}${c.value !== null ? ` ${c.value}` : ""} back` });
  for (const e of before.effects) if (!after.effects.some((x) => x.id === e.id)) out.push({ kind: "effect", text: `${e.name} removed` });
  for (const e of after.effects) if (!before.effects.some((x) => x.id === e.id)) out.push({ kind: "effect", text: `${e.name} back` });
  if (before.heroPoints !== after.heroPoints) out.push({ kind: "hero-point", text: `Hero Points ${before.heroPoints} → ${after.heroPoints}` });
  if (before.focus !== after.focus) out.push({ kind: "focus", text: `Focus Points ${before.focus} → ${after.focus}` });
  if (before.round !== after.round) out.push({ kind: "encounter", text: after.round === null ? "Encounter ends" : before.round === null ? `Encounter begins (round ${after.round})` : `Round ${before.round} → ${after.round}` });
  else if (before.clock !== after.clock) out.push({ kind: "time", text: `Clock ${before.clock}s → ${after.clock}s` });
  for (const s of before.consumables) {
    const still = after.consumables.find((x) => x.id === s.id);
    if (!still) out.push({ kind: "consumable", text: `${s.name} gone` });
    else if (still.quantity !== s.quantity || still.uses?.value !== s.uses?.value) out.push({ kind: "consumable", text: `${s.name}: ${still.quantity}${still.uses ? ` (${still.uses.value} / ${still.uses.max} uses)` : ""}` });
  }
  for (const s of after.consumables) if (!before.consumables.some((x) => x.id === s.id)) out.push({ kind: "consumable", text: `${s.name} ×${s.quantity} back` });
  if (JSON.stringify(before.spellcasting) !== JSON.stringify(after.spellcasting)) out.push({ kind: "slot", text: "Spell slots changed" });
  if (JSON.stringify(before.resources) !== JSON.stringify(after.resources)) out.push({ kind: "resource", text: "Resources changed" });
  if (JSON.stringify(before.uses) !== JSON.stringify(after.uses)) out.push({ kind: "use", text: "Uses changed" });
  if (JSON.stringify(before.toggles) !== JSON.stringify(after.toggles)) out.push({ kind: "toggle", text: "Toggles changed" });
  return out;
}

/* ------------------------------------ Dependencies ------------------------------------ */

/**
 * The parts of the Session a Play Action touches, as keys: "hp", "condition:<id>", "hero-points",
 * "slots:<entry>:<rank>" ... Primitives set one thing each, so a batch's keys are the union; the
 * id counter is left out (allocation skips ids in use, so a stale counter is harmless). A table
 * action, whose steps are not known without applying it, touches "*" (everything), as `replace`
 * does. Two entries with disjoint keys commute exactly.
 */
export function touchedKeys(action: PlayAction, into: Set<string> = new Set()): Set<string> {
  if (!isPrimitive(action)) {
    into.add("*");
    return into;
  }
  switch (action.kind) {
    case "set-hp": into.add("hp"); break;
    case "add-condition": into.add(`condition:${action.condition.id}`); break;
    case "remove-condition":
    case "set-condition-value": into.add(`condition:${action.id}`); break;
    case "add-effect": into.add(`effect:${action.effect.id}`); break;
    case "remove-effect":
    case "set-effect": into.add(`effect:${action.id}`); break;
    case "set-hero-points": into.add("hero-points"); break;
    case "set-focus": into.add("focus"); break;
    case "set-resource": into.add(`resource:${action.key}`); break;
    case "set-uses": into.add(`uses:${action.uuid}`); break;
    case "set-slots": into.add(`slots:${action.entry}:${action.rank}`); break;
    case "set-prepared": into.add(`prepared:${action.entry}:${action.rank}:${action.index}`); break;
    case "set-innate": into.add(`innate:${action.entry}:${action.spell}`); break;
    case "set-charges": into.add(`charges:${action.entry}`); break;
    case "add-consumable": into.add(`consumable:${action.stack.id}`); break;
    case "remove-consumable":
    case "set-consumable": into.add(`consumable:${action.id}`); break;
    case "set-clock": into.add("clock"); break;
    case "set-toggle": into.add(`toggle:${action.key}`); break;
    case "set-counter": break;
    case "replace": into.add("*"); break;
    case "batch": for (const step of action.steps) touchedKeys(step, into); break;
  }
  return into;
}

function intersects(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.has("*") && b.size > 0) return true;
  if (b.has("*") && a.size > 0) return true;
  for (const k of a) if (b.has(k)) return true;
  return false;
}

/**
 * Whether an entry's effect currently stands: it has an inverse and nothing reversed it. A
 * reversal entry has no inverse of its own (reversals are final; an undone action is done again
 * by acting again), so the standing entries are exactly the un-reversed originals.
 */
export function isLive(entry: LogEntry): boolean {
  return entry.inverse !== null && entry.reversedBy === null;
}

export interface ReversalPlan {
  entry: LogEntry;
  /** Later live entries whose effect depends on this one, oldest first; reversed newest first, before the entry itself. */
  dependents: LogEntry[];
  /** The order to apply: the dependents newest first, then the entry. */
  order: LogEntry[];
}

/**
 * What reversing an entry takes. A later live entry depends on it when they touch the same part
 * of the Session (a Hero Point spent after the reroll that spent one), and on anything those
 * dependents touch in turn; such entries are reversed first, newest first, so every inverse meets
 * the state it was made from. Null when the entry is not reversible (a note, or already reversed).
 */
export function reversalPlan(log: RollLog, id: string): ReversalPlan | null {
  const index = log.entries.findIndex((e) => e.id === id);
  const entry = log.entries[index];
  if (!entry || !isLive(entry)) return null;
  const keys = touchedKeys(entry.inverse!);
  const dependents: LogEntry[] = [];
  for (const later of log.entries.slice(index + 1)) {
    if (!isLive(later)) continue;
    const theirs = touchedKeys(later.inverse!);
    if (theirs.size === 0 || !intersects(keys, theirs)) continue;
    dependents.push(later);
    for (const k of theirs) keys.add(k);
  }
  return { entry, dependents, order: [...dependents].reverse().concat(entry) };
}

/** The newest entry whose effect stands and can be undone on its own (nothing after it depends on it). */
export function newestUndoable(log: RollLog): LogEntry | null {
  for (let i = log.entries.length - 1; i >= 0; i--) {
    const entry = log.entries[i]!;
    if (!isLive(entry)) continue;
    const plan = reversalPlan(log, entry.id);
    return plan && plan.dependents.length === 0 ? entry : null;
  }
  return null;
}

/** Entries matching a filter, newest first. */
export function filterEntries(log: RollLog, filter: "all" | "roll" | "session"): LogEntry[] {
  const list = filter === "all" ? log.entries : log.entries.filter((e) => e.type === filter || e.type === "note");
  return [...list].reverse();
}

export interface LogGroup {
  /** The round, or null outside encounters. */
  round: number | null;
  entries: LogEntry[];
}

/** Consecutive runs of one round, newest first, for the grouped view. */
export function groupByRound(entries: readonly LogEntry[]): LogGroup[] {
  const groups: LogGroup[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.round === entry.round) last.entries.push(entry);
    else groups.push({ round: entry.round, entries: [entry] });
  }
  return groups;
}
